/**
 * Why a PMS sync failed, as far as the failed-sync notice needs to know.
 *
 * property-service's `POST /properties/assets/sync` answers every failure with
 * a `SyncResponse` whose `errorMessage` names the reason, but the two kinds
 * need different advice:
 *
 * - `upload-failed` — a 500. Every failure in the upload step (bad credentials,
 *   a vendor rejection, a missing connection) is the same generic 500 on
 *   purpose (the real error stays in the log), so its message says nothing the
 *   notice's own "check the connection" copy doesn't. Also the fallback for a
 *   response the controller never wrote: an empty-bodied policy-enforcer 403,
 *   a WAF page, a network failure.
 * - `refused` — any other status with a reason (DV-3334's account refusals,
 *   the paused 409). The connection is not the problem, so the notice shows
 *   the server's reason and does not point at the connection.
 */
export type SyncFailure = { kind: 'upload-failed' } | { kind: 'refused'; message: string };

const UPLOAD_FAILED: SyncFailure = { kind: 'upload-failed' };

function reasonIn(data: unknown): string | null {
  if (typeof data !== 'object' || data === null || !('errorMessage' in data)) return null;
  const { errorMessage } = data;
  if (typeof errorMessage !== 'string' || errorMessage.trim() === '') return null;
  return errorMessage;
}

/** Classifies what the sync mutation's `unwrap()` rejected with. */
export function syncFailureFrom(error: unknown): SyncFailure {
  if (typeof error !== 'object' || error === null || !('status' in error)) return UPLOAD_FAILED;
  const { status } = error;
  if (typeof status !== 'number' || status === 500) return UPLOAD_FAILED;
  const message = reasonIn('data' in error ? error.data : undefined);
  return message === null ? UPLOAD_FAILED : { kind: 'refused', message };
}
